import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["otherChoice", "otherAnswer", "otherAnswerInput"]

  connect() {
    this.toggleOtherAnswer()
  }

  toggleOtherAnswer() {
    if (!this.hasOtherAnswerTarget || !this.hasOtherChoiceTarget) return

    const selected = this.otherChoiceTargets.some((choice) => choice.checked)

    this.otherAnswerTargets.forEach((answer) => {
      answer.hidden = !selected
      answer.setAttribute("aria-hidden", selected ? "false" : "true")
    })

    this.otherAnswerInputTargets.forEach((input) => {
      const controlDisabled = input.dataset.surveyBlockItemPreviewControlDisabled === "true"
      input.disabled = controlDisabled || !selected
      if (!selected) input.value = ""
    })
  }

  selectStarRating(event) {
    event.preventDefault()

    const selectedValue = Number.parseInt(event.currentTarget.dataset.surveyStarRatingValue || "", 10)
    if (Number.isNaN(selectedValue)) return

    this.element.querySelectorAll("[data-survey-star-rating-value]").forEach((option) => {
      const optionValue = Number.parseInt(option.dataset.surveyStarRatingValue || "", 10)
      const selected = optionValue <= selectedValue
      option.classList.toggle("is-selected", selected)
      option.setAttribute("aria-checked", optionValue === selectedValue ? "true" : "false")

      const icon = option.querySelector(".ph-star")
      if (!icon) return

      icon.classList.toggle("ph-fill", selected)
      icon.classList.toggle("ph-thin", !selected)
    })
  }

  selectNumericScale(event) {
    event.preventDefault()

    const selectedValue = Number.parseInt(event.currentTarget.dataset.surveyNumericScaleValue || "", 10)
    if (Number.isNaN(selectedValue)) return

    this.element.querySelectorAll("[data-survey-numeric-scale-value]").forEach((option) => {
      const optionValue = Number.parseInt(option.dataset.surveyNumericScaleValue || "", 10)
      option.classList.toggle("is-selected", optionValue <= selectedValue)
      option.setAttribute("aria-checked", optionValue === selectedValue ? "true" : "false")
    })
  }

  startRankingDrag(event) {
    const option = event.currentTarget.closest("[data-survey-ranking-preview-option]")
    if (!option) return

    this.draggedRankingOption = option
    option.classList.add("is-dragging")
    option.closest("[data-survey-ranking-preview-list]")?.classList.add("is-dragging")
    event.dataTransfer?.setData("text/plain", "ranking-preview-option")
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "move"
  }

  dragRankingOver(event) {
    const draggedOption = this.draggedRankingOption
    if (!draggedOption) return

    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"

    const list = event.currentTarget.closest("[data-survey-ranking-preview-list]")
    if (!list) return

    const targetOption = event.target.closest("[data-survey-ranking-preview-option]")
    if (!targetOption || targetOption === draggedOption || !list.contains(targetOption)) {
      this.clearRankingDropIndicators(list)
      return
    }

    const targetRect = targetOption.getBoundingClientRect()
    const insertAfter = event.clientY > targetRect.top + targetRect.height / 2
    this.setRankingDropIndicator(list, targetOption, insertAfter ? "after" : "before")
  }

  leaveRankingDrag(event) {
    const list = event.currentTarget.closest("[data-survey-ranking-preview-list]")
    if (!list || (event.relatedTarget && list.contains(event.relatedTarget))) return

    this.clearRankingDropIndicators(list)
  }

  dropRanking(event) {
    const draggedOption = this.draggedRankingOption
    if (!draggedOption) return

    event.preventDefault()

    const list = event.currentTarget.closest("[data-survey-ranking-preview-list]")
    const indicatedOption = list?.querySelector(".is-drop-before, .is-drop-after")
    const targetOption = indicatedOption || event.target.closest("[data-survey-ranking-preview-option]")
    if (!list || !targetOption || targetOption === draggedOption || !list.contains(targetOption)) return

    let insertAfter = targetOption.classList.contains("is-drop-after")
    if (!insertAfter && !targetOption.classList.contains("is-drop-before")) {
      const targetRect = targetOption.getBoundingClientRect()
      insertAfter = event.clientY > targetRect.top + targetRect.height / 2
    }

    targetOption.insertAdjacentElement(insertAfter ? "afterend" : "beforebegin", draggedOption)
    this.clearRankingDropIndicators(list)
    this.syncRankingPositions(list)
  }

  endRankingDrag(event) {
    const list = event.currentTarget.closest("[data-survey-ranking-preview-list]")
    event.currentTarget.classList.remove("is-dragging")
    list?.classList.remove("is-dragging")
    this.clearRankingDropIndicators(list)
    this.draggedRankingOption = null
    this.syncRankingPositions(list)
  }

  moveRanking(event) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return

    const option = event.currentTarget.closest("[data-survey-ranking-preview-option]")
    const list = option?.closest("[data-survey-ranking-preview-list]")
    if (!option || !list) return

    event.preventDefault()

    if (event.key === "ArrowUp" && option.previousElementSibling) {
      list.insertBefore(option, option.previousElementSibling)
    } else if (event.key === "ArrowDown" && option.nextElementSibling) {
      option.nextElementSibling.insertAdjacentElement("afterend", option)
    } else if (event.key === "Home" && list.firstElementChild !== option) {
      list.insertBefore(option, list.firstElementChild)
    } else if (event.key === "End" && list.lastElementChild !== option) {
      list.appendChild(option)
    }

    this.syncRankingPositions(list)
    option.focus()
  }

  syncRankingPositions(list) {
    if (!list) return

    list.querySelectorAll("[data-survey-ranking-preview-option]").forEach((option, index) => {
      const position = option.querySelector(".survey-block-item__ranking-position")
      if (position) position.textContent = (index + 1).toString()
    })
  }

  clearRankingDropIndicators(list) {
    if (!list) return

    if (this.rankingDropIndicator?.option && list.contains(this.rankingDropIndicator.option)) {
      this.rankingDropIndicator.option.classList.remove("is-drop-before", "is-drop-after")
      this.rankingDropIndicator = null
      return
    }

    list.querySelectorAll(".is-drop-before, .is-drop-after").forEach((option) => {
      option.classList.remove("is-drop-before", "is-drop-after")
    })
    this.rankingDropIndicator = null
  }

  setRankingDropIndicator(list, option, direction) {
    const activeClass = direction === "after" ? "is-drop-after" : "is-drop-before"
    const inactiveClass = direction === "after" ? "is-drop-before" : "is-drop-after"
    if (this.rankingDropIndicator?.option === option && this.rankingDropIndicator.direction === direction && option.classList.contains(activeClass) && !option.classList.contains(inactiveClass)) return

    this.clearRankingDropIndicators(list)
    option.classList.add(activeClass)
    this.rankingDropIndicator = { option, direction }
  }
}
